import { IdentifiableObject } from '../types'

export type Priority = 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT'

export type VendorTaskStatus =
    | 'ASSIGNED'
    | 'IN_PROGRESS'
    | 'ON_HOLD'
    | 'COMPLETED'

// Subset of the User shape we get back on assigned_by / assigned_to
export interface VendorProjectUserStub {
    id: number
    name_field: string
    email_field: string | null
}

export interface VendorTask extends IdentifiableObject {
    title: string
    description?: string | null
    assigned_by: VendorProjectUserStub
    assigned_to: VendorProjectUserStub
    assigned_to_id?: number
    start_time?: string | null
    end_time?: string | null
    schedule_time?: string | null
    address?: string | null
    status: VendorTaskStatus
    project?: number | null
    created_date: string
    modified_date: string
    message_count?: number | null
    channel?: number | null
}

export interface VendorProjectSourceDocument {
    id: number
    document: string
    name: string
    source_description: string | null
    status: 'DRAFT' | 'CONFIRMED' | 'FAILED'
    created_date?: string
}

export interface VendorProject extends IdentifiableObject {
    title: string
    description: string
    assigned_by: VendorProjectUserStub
    start_date: string | null
    end_date: string | null
    priority: Priority
    address: string
    created_date: string
    modified_date: string
    tasks?: VendorTask[]
    message_count?: number | null
    channel?: number | null
    source_document?: VendorProjectSourceDocument | null
    // derived (computed client-side, see services.recomputeTaskCounts)
    taskCount?: number
    completedTaskCount?: number
    inProgressTaskCount?: number
}

export interface VendorProjectFormData {
    title: string
    description?: string
    start_date: string
    end_date: string
    priority: Priority
    address?: string
}

export interface VendorProjectAiProjectDraft {
    title: string
    description: string
    start_date: string | null
    end_date: string | null
    priority: Priority
    address: string
}

export interface VendorProjectAiTaskDraft {
    title: string
    description: string
    schedule_time: string | null
    address: string
}

export interface VendorProjectAiDraft {
    project: VendorProjectAiProjectDraft
    tasks: VendorProjectAiTaskDraft[]
}

export interface VendorProjectDocument {
    id: number
    company?: number
    uploaded_by?: number
    confirmed_project: number | null
    document: string | null
    name: string
    source_description: string | null
    ai_draft: VendorProjectAiDraft | null
    status: 'DRAFT' | 'CONFIRMED' | 'FAILED'
    error_message: string
    created_date?: string
    modified_date?: string
}

export interface GenerateProjectDraftInput {
    file?: File | null
    description?: string | null
}

export interface ConfirmGeneratedPayload {
    document_id?: number
    assigned_to_id?: number
    project: VendorProjectAiProjectDraft
    tasks: VendorProjectAiTaskDraft[]
}

export interface CreateTaskData {
    title: string
    description?: string | null
    assigned_to_id: number
    start_time?: string | null
    end_time?: string | null
    schedule_time?: string | null
    address?: string | null
    status?: VendorTaskStatus | 'CANCELLED'
}

export type UpdateTaskData = Partial<CreateTaskData>

export interface FilterCriteria {
    dateFrom?: string | null
    dateTo?: string | null
    status?: VendorTaskStatus[] | null
    priority?: Priority[] | null
    userId?: string | null
    sortBy?: VendorProjectSortField | null
    sortOrder?: 'asc' | 'desc' | null
}

export type VendorProjectSortField =
    | 'title'
    | 'priority'
    | 'start_date'
    | 'end_date'
    | 'created_at'
    | 'updated_at'
    | 'assigned_to_name'
    | 'assigned_by_name'

export const ALL_PRIORITIES: Priority[] = ['LOW', 'MEDIUM', 'HIGH', 'URGENT']

export const ALL_VENDOR_TASK_STATUSES: VendorTaskStatus[] = [
    'ASSIGNED',
    'IN_PROGRESS',
    'ON_HOLD',
    'COMPLETED',
]

/**
 * Status palette tuned for the web app. The mobile guide (§5.1) lists raw hex
 * pairs; we expand them into a richer token set so the UI can use a strong
 * accent (`fg`), a soft tint background (`bg`) and a darker on-tint text color
 * (`text`) without having to hand-pick contrast every place.
 */
export interface VendorStatusColorToken {
    fg: string
    bg: string
    text: string
}

export const VENDOR_TASK_STATUS_COLORS: Record<
    VendorTaskStatus,
    VendorStatusColorToken
> = {
    COMPLETED: { fg: '#2e7d32', bg: '#e8f5e9', text: '#1b5e20' },
    IN_PROGRESS: { fg: '#1976d2', bg: '#e3f2fd', text: '#0d47a1' },
    ASSIGNED: { fg: '#ed6c02', bg: '#fff4e5', text: '#7a3e00' },
    ON_HOLD: { fg: '#7b1fa2', bg: '#f3e5f5', text: '#4a148c' },
}

export interface VendorPriorityColorToken {
    fg: string
    bg: string
    text: string
}

export const PRIORITY_COLORS: Record<Priority, VendorPriorityColorToken> = {
    LOW: { fg: '#388e3c', bg: '#e8f5e9', text: '#1b5e20' },
    MEDIUM: { fg: '#1976d2', bg: '#e3f2fd', text: '#0d47a1' },
    HIGH: { fg: '#ed6c02', bg: '#fff4e5', text: '#7a3e00' },
    URGENT: { fg: '#d32f2f', bg: '#ffebee', text: '#b71c1c' },
}

export const VENDOR_TASK_STATUS_LABELS: Record<VendorTaskStatus, string> = {
    ASSIGNED: 'Assigned',
    IN_PROGRESS: 'In Progress',
    ON_HOLD: 'On Hold',
    COMPLETED: 'Completed',
}

export const PRIORITY_LABELS: Record<Priority, string> = {
    LOW: 'Low',
    MEDIUM: 'Medium',
    HIGH: 'High',
    URGENT: 'Urgent',
}
